import React, { useState, useEffect, useRef } from 'react';
import { 
  Form, Button, Alert, Spinner, Card, 
  ListGroup, Table, Row, Col, Modal,
  Badge, InputGroup
} from 'react-bootstrap';
import { 
  Search, XCircle, Printer, 
  CheckCircle, CreditCard, Cash
} from 'react-bootstrap-icons';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import ErrorBoundary from './components/ErrorBoundary';
import { generateReceipt } from '../utils/GenerateReceipt';
import { useNavigate } from 'react-router-dom';
import './SalesManagement.css';

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

export default function SalesManagement() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [stockItems, setStockItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [paymentType, setPaymentType] = useState('cash');
  const [customer, setCustomer] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [fetchingStock, setFetchingStock] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [processingSale, setProcessingSale] = useState(false);
  const navigate = useNavigate();

  // Create audio references
  const clickSoundRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);

  // Initialize audio with mobile-specific handling
  useEffect(() => {
    // Create audio context
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioContextRef.current = new AudioContext();
    const audioCtx = audioContextRef.current;

    // Create HTML audio element for browsers that don't support Web Audio API
    clickSoundRef.current = new Audio('/sound/clicking.mp3');
    clickSoundRef.current.preload = 'auto';
    clickSoundRef.current.playsInline = true; // Essential for iOS

    // Handle audio loading
    const handleCanPlay = () => setAudioReady(true);
    const handleError = (e) => console.error('Audio error:', e);

    clickSoundRef.current.addEventListener('canplaythrough', handleCanPlay);
    clickSoundRef.current.addEventListener('error', handleError);

    // Load audio for Web Audio API
    const loadAudioBuffer = async () => {
      try {
        const response = await fetch('/sound/clicking.mp3');
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        audioBufferRef.current = audioBuffer;
      } catch (error) {
        console.error('Failed to load audio:', error);
      }
    };

    loadAudioBuffer();

    // Unlock audio on first user interaction
    const unlockAudio = () => {
      // Resume suspended audio context
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().then(() => {
          console.log('Audio context resumed successfully');
        });
      }

      // Play/press to unlock HTML audio on iOS
      clickSoundRef.current.play().then(() => {
        clickSoundRef.current.pause();
        clickSoundRef.current.currentTime = 0;
      }).catch(e => console.log('Unlock attempt:', e));
    };

    // Add event listeners for unlocking audio
    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('touchstart', unlockAudio, { once: true });

    return () => {
      // Clean up event listeners
      clickSoundRef.current.removeEventListener('canplaythrough', handleCanPlay);
      clickSoundRef.current.removeEventListener('error', handleError);
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      
      // Close audio context
      if (audioCtx.state !== 'closed') {
        audioCtx.close();
      }
    };
  }, []);

  // Play sound using the best available method
  const playClickSound = () => {
    if (!audioReady) return;

    try {
      // Try Web Audio API first for better mobile support
      if (audioBufferRef.current && audioContextRef.current) {
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBufferRef.current;
        source.connect(audioContextRef.current.destination);
        source.start(0);
        return;
      }

      // Fallback to HTML5 audio
      if (clickSoundRef.current) {
        clickSoundRef.current.currentTime = 0;
        clickSoundRef.current.play().catch(error => {
          console.error('HTML5 audio error:', error);
        });
      }
    } catch (error) {
      console.error('Sound playback failed:', error);
      
      // Final fallback: simple beep
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, ctx.currentTime);
        oscillator.connect(ctx.destination);
        oscillator.start();
        setTimeout(() => oscillator.stop(), 100);
      } catch (fallbackError) {
        console.error('Fallback sound failed:', fallbackError);
      }
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchItems();
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchItems = async () => {
    setFetchingStock(true);
    try {
      const { data } = await api.get('/stock', { 
        params: { page: 1, limit: 0, search: searchTerm, sortBy: 'type', sortOrder: 'asc' }
      });
      setStockItems(data?.items || []);
      setError('');
    } catch (err) {
      setError('Failed to load inventory. Please try again.');
    } finally {
      setFetchingStock(false);
    }
  };

  const addItemToSale = (item) => {
    const existingItem = selectedItems.find(i => i.itemId === item._id);
    const currentStock = stockItems.find(si => si._id === item._id)?.quantity || 0;

    if (existingItem) {
      const newQty = existingItem.quantity + 1;
      if (newQty > currentStock) {
        setError(`Only ${currentStock} items available for ${item.type}`);
        return;
      }
      setSelectedItems(prev => prev.map(i => 
        i.itemId === item._id ? { ...i, quantity: newQty } : i
      ));
    } else {
      if (currentStock < 1) {
        setError(`${item.type} is out of stock`);
        return;
      }
      setSelectedItems(prev => [
        ...prev,
        {
          itemId: item._id,
          type: item.type,
          quantity: 1,
          unitPrice: item.sellingPrice,
          costPrice: item.cost,
          currentStock
        }
      ]);
    }
    
    // Play sound effect after validation passes
    playClickSound();
  };

  const updateItemQuantity = (index, quantity) => {
    const newItems = [...selectedItems];
    const item = newItems[index];
    const currentStock = stockItems.find(si => si._id === item.itemId)?.quantity || 0;

    const parsedQty = Math.min(
      Math.max(parseInt(quantity) || 0, 0),
      currentStock
    );

    if (parsedQty > currentStock) {
      setError(`Only ${currentStock} items available for ${item.type}`);
      return;
    }

    newItems[index].quantity = parsedQty;
    setSelectedItems(newItems);
  };

  const removeItem = (index) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== index));
  };

  const { totalRevenue, totalProfit, totalItems } = selectedItems.reduce(
    (acc, item) => {
      const qty = item.quantity;
      const revenue = qty * item.unitPrice;
      const cost = qty * item.costPrice;
      return {
        totalRevenue: acc.totalRevenue + revenue,
        totalProfit: acc.totalProfit + (revenue - cost),
        totalItems: acc.totalItems + qty
      };
    },
    { totalRevenue: 0, totalProfit: 0, totalItems: 0 }
  );

  const processSale = async () => {
    setProcessingSale(true);

    try {
      const payload = {
        items: selectedItems.map(item => ({
          itemId: item.itemId,
          quantity: item.quantity
        })),
        paymentType,
        totalAmount: totalRevenue.toString(),
        totalProfit: totalProfit.toString(),
        saleDate: new Date().toISOString(),
        ...(paymentType === 'credit' && {
          customer: {
            name: customer,
            creditTerms: {
              dueDate: new Date(dueDate).toISOString()
            }
          }
        })
      };

      const response = await api.post('/sales', payload);
      if (response.status === 201) {
        await fetchItems();
        setShowSuccess(true);
        setSelectedItems([]);
        setError('');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        err.response?.data?.error?.message || 
        'Failed to process sale. Please check your input and try again.';
      setError(errorMessage);
    } finally {
      setProcessingSale(false);
      setShowConfirmation(false);
    }
  };

  const handleCompleteSale = () => {
    // Validate before showing confirmation
    if (selectedItems.length === 0) {
      setError('Please add at least one item to complete sale');
      return;
    }
    
    if (paymentType === 'credit' && !customer) {
      setError('Customer name is required for credit sales');
      return;
    }
    
    if (paymentType === 'credit' && !dueDate) {
      setError('Due date is required for credit sales');
      return;
    }
    
    setShowConfirmation(true);
  };

  const generatePDFReceipt = () => {
    generateReceipt({
      businessName: user?.profile?.businessName || 'Default Business',
      saleDate: new Date(),
      items: selectedItems.map(item => ({
        type: item.type,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      })),
      total: totalRevenue,
      filenamePrefix: 'sales-receipt'
    });
  };

  const generateJPEGReceipt = () => {
    generateReceipt({
      items: selectedItems,
      user,
      paymentType,
      customer,
      dueDate,
      totalAmount: totalRevenue,
      totalProfit,
      format: 'jpeg',
    });
  };

  if (showSuccess) {
    return (
      <div className={`text-center mt-4 px-${isMobile ? '2' : '4'}`}>
        <Alert variant="success" className="py-3">
          <CheckCircle size={32} className="mb-3 text-success" />
          <Alert.Heading className="fs-4">Sale Completed Successfully!</Alert.Heading>
          <div className="d-flex flex-column flex-sm-row justify-content-center gap-2 mt-3">
            <Button 
              variant="success" 
              onClick={() => setShowSuccess(false)}
              className="py-2"
            >
              Create New Sale
            </Button>
            <Button 
              variant="primary" 
              onClick={() => navigate('/sales-list')}
              className="py-2"
            >
              View Sales History
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  const renderInventoryRow = (item) => {
    const isOutOfStock = item.quantity === 0;
    return (
      <tr key={item._id} className={isOutOfStock ? 'table-secondary' : ''}>
        <td>{item.type}</td>
        <td>Ksh{item.sellingPrice.toFixed(2)}</td>
        <td className={isOutOfStock ? 'text-danger fw-bold' : ''}>
          {item.quantity}
        </td>
        <td>
          <Button
            size={isMobile ? "sm" : undefined}
            onClick={() => addItemToSale(item)}
            disabled={isOutOfStock}
            variant={isOutOfStock ? 'secondary' : 'primary'}
            className={isMobile ? "w-100" : ""}
          >
            {isOutOfStock ? 'Out of Stock' : 'Sell'}
          </Button>
        </td>
      </tr>
    );
  };

  const MobileInventoryList = () => (
    <ListGroup className="mb-3">
      {stockItems.map(item => {
        const isOutOfStock = item.quantity === 0;
        return (
          <ListGroup.Item 
            key={item._id} 
            className={`d-flex justify-content-between align-items-center ${isOutOfStock ? 'bg-light' : ''}`}
          >
            <div>
              <div className="fw-bold">{item.type}</div>
              <div className="d-flex gap-2 small">
                <span>Ksh{item.sellingPrice.toFixed(2)}</span>
                <span className={isOutOfStock ? 'text-danger' : ''}>
                  Stock: {item.quantity}
                </span>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => addItemToSale(item)}
              disabled={isOutOfStock}
              variant={isOutOfStock ? 'secondary' : 'primary'}
            >
              Sell
            </Button>
          </ListGroup.Item>
        );
      })}
    </ListGroup>
  );

  const renderSelectedItemRow = (item, index) => {
    const currentStock = stockItems.find(si => si._id === item.itemId)?.quantity || 0;

    return (
      <tr key={index}>
        <td>{item.type}</td>
        <td>
          <Form.Control
            type="number"
            min="1"
            max={currentStock}
            value={item.quantity}
            onChange={(e) => updateItemQuantity(index, e.target.value)}
            className={item.quantity === currentStock ? 'border-warning' : ''}
            size={isMobile ? "sm" : undefined}
          />
          <div className="text-muted small mt-1">
            Available: {currentStock}
          </div>
        </td>
        <td>Ksh{item.unitPrice.toFixed(2)}</td>
        <td>Ksh{(item.quantity * item.unitPrice).toFixed(2)}</td>
        <td>
          <Button
            variant="danger"
            size="sm"
            onClick={() => removeItem(index)}
            title="Remove item"
          >
            &times;
          </Button>
        </td>
      </tr>
    );
  };

  const MobileSelectedItems = () => (
    <ListGroup className="mb-3">
      {selectedItems.map((item, index) => {
        const currentStock = stockItems.find(si => si._id === item.itemId)?.quantity || 0;
        
        return (
          <ListGroup.Item key={index} className="position-relative">
            <div className="d-flex justify-content-between align-items-start">
              <div className="w-75">
                <div className="fw-bold">{item.type}</div>
                <div className="d-flex gap-2 small mt-1">
                  <div>Price: <span className="text-success">Ksh{item.unitPrice.toFixed(2)}</span></div>
                  <div>Total: <span className="text-primary">Ksh{(item.quantity * item.unitPrice).toFixed(2)}</span></div>
                </div>
              </div>
              
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="number"
                  min="1"
                  max={currentStock}
                  value={item.quantity}
                  onChange={(e) => updateItemQuantity(index, e.target.value)}
                  className={item.quantity === currentStock ? 'border-warning' : ''}
                  size="sm"
                  style={{ width: '70px' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeItem(index)}
                  className="p-1"
                >
                  <XCircle size={18} />
                </Button>
              </div>
            </div>
          </ListGroup.Item>
        );
      })}
    </ListGroup>
  );

  return (
    <ErrorBoundary>
      <div className={`mt-3 px-${isMobile ? '2' : '4'}`}>
        {/* Confirmation Modal */}
        <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Sale Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h5 className="mb-3">Sale Summary</h5>
            
            <ListGroup className="mb-3">
              {selectedItems.map((item, index) => (
                <ListGroup.Item 
                  key={index}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <div className="fw-bold">{item.type}</div>
                    <div className="d-flex gap-2">
                      <span>{item.quantity} × Ksh{item.unitPrice.toFixed(2)}</span>
                      <span className="text-success fw-bold">
                        = Ksh{(item.quantity * item.unitPrice).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      removeItem(index);
                      if (selectedItems.length === 1) {
                        setShowConfirmation(false);
                      }
                    }}
                    title="Remove item"
                  >
                    <XCircle size={16} />
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
            
            <div className="border-top pt-2">
              <div className="d-flex justify-content-between">
                <span>Total Items:</span>
                <strong>{totalItems}</strong>
              </div>
              <div className="d-flex justify-content-between text-success">
                <span>Total Amount:</span>
                <strong>Ksh{totalRevenue.toFixed(2)}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Payment Method:</span>
                <strong className="text-capitalize">{paymentType}</strong>
              </div>
              
              {paymentType === 'credit' && (
                <>
                  <div className="d-flex justify-content-between">
                    <span>Customer:</span>
                    <strong>{customer}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Due Date:</span>
                    <strong>{new Date(dueDate).toLocaleDateString()}</strong>
                  </div>
                </>
              )}
            </div>
            
            <div className="d-flex flex-wrap gap-2 mt-3">
              <Button
                variant="outline-primary"
                className="d-flex align-items-center gap-1"
                onClick={generatePDFReceipt}
                disabled={processingSale}
              >
                <Printer size={16} /> PDF Receipt
              </Button>
              
              <Button
                variant="outline-success"
                className="d-flex align-items-center gap-1"
                onClick={generateJPEGReceipt}
                disabled={processingSale}
              >
                <Printer size={16} /> JPEG Receipt
              </Button>
            </div>
            
            <Alert variant="warning" className="mt-3">
              <Alert.Heading>Please verify all details</Alert.Heading>
              <p className="mb-0">
                Once confirmed, this sale will be recorded and stock quantities will be updated.
              </p>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowConfirmation(false)}
              disabled={processingSale}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={processSale}
              disabled={processingSale}
            >
              {processingSale ? (
                <>
                  <Spinner as="span" animation="border" size="sm" /> Processing...
                </>
              ) : (
                <>
                  <CheckCircle size={20} className="me-1" />
                  Confirm Sale
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0 fs-5">Create New Sale</h3>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate('/sales-list')}
          >
            Sales Records
          </Button>
        </div>

        {error && (
          <Alert variant="danger" className="py-2" dismissible onClose={() => setError('')}>
            <small>{error}</small>
          </Alert>
        )}

        <Row className="g-3">
          <Col lg={8} md={7}>
            <Card className="shadow-sm">
              <Card.Body className="p-2 p-sm-3">
                <Form.Group className="mb-3">
                  <InputGroup>
                    <InputGroup.Text>
                      <Search />
                    </InputGroup.Text>
                    <Form.Control
                      type="search"
                      placeholder="Search items by name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size={isMobile ? "sm" : undefined}
                    />
                  </InputGroup>
                </Form.Group>

                {fetchingStock ? (
                  <div className="text-center my-4">
                    <Spinner animation="border" variant="primary" size={isMobile ? "sm" : undefined} />
                    <p className="mt-2 small">Loading inventory...</p>
                  </div>
                ) : isMobile ? (
                  <MobileInventoryList />
                ) : (
                  <div className="table-responsive">
                    <Table striped hover responsive>
                      <thead className="table-light">
                        <tr>
                          <th>Item Name</th>
                          <th>Price</th>
                          <th>Stock</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockItems.map(renderInventoryRow)}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>

            {selectedItems.length > 0 && (
              <Card className="mt-3 shadow-sm">
                <Card.Body className="p-2 p-sm-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 fs-6">Selected Items</h5>
                    <div className="small text-muted">{user?.profile?.displayName}</div>
                  </div>

                  {isMobile ? (
                    <MobileSelectedItems />
                  ) : (
                    <Table bordered responsive>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Total</th>
                          <th>Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedItems.map(renderSelectedItemRow)}
                      </tbody>
                    </Table>
                  )}

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button
                      variant="outline-primary"
                      className="d-flex align-items-center gap-1"
                      onClick={generatePDFReceipt}
                      size={isMobile ? "sm" : undefined}
                    >
                      <Printer size={16} /> PDF Receipt
                    </Button>

                    <Button
                      variant="outline-success"
                      className="d-flex align-items-center gap-1"
                      onClick={generateJPEGReceipt}
                      size={isMobile ? "sm" : undefined}
                    >
                      <Printer size={16} /> JPEG Receipt
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col lg={4} md={5}>
            <Card className="shadow-sm" style={isMobile ? {} : { position: 'sticky', top: '20px' }}>
              <Card.Body className="p-2 p-sm-3">
                <h4 className="mb-3 fs-5">Transaction Summary</h4>
                <ListGroup className="mb-3">
                  <ListGroup.Item className="d-flex justify-content-between align-items-center py-2">
                    <span>Items:</span>
                    <Badge bg="primary" pill>{totalItems}</Badge>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between align-items-center py-2 text-success">
                    <span>Revenue:</span>
                    <strong>Ksh{totalRevenue.toFixed(2)}</strong>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between align-items-center py-2 text-primary">
                    <span>Profit:</span>
                    <strong>Ksh{totalProfit.toFixed(2)}</strong>
                  </ListGroup.Item>
                </ListGroup>

                <Form.Group className="mb-3">
                  <Form.Label>Payment Method</Form.Label>
                  <div className="d-flex gap-2">
                    <Button
                      variant={paymentType === 'cash' ? 'primary' : 'outline-primary'}
                      className="d-flex align-items-center gap-1 flex-grow-1"
                      onClick={() => setPaymentType('cash')}
                    >
                      <Cash size={18} /> Cash
                    </Button>
                    <Button
                      variant={paymentType === 'credit' ? 'primary' : 'outline-primary'}
                      className="d-flex align-items-center gap-1 flex-grow-1"
                      onClick={() => setPaymentType('credit')}
                    >
                      <CreditCard size={18} /> Credit
                    </Button>
                  </div>
                </Form.Group>

                {paymentType === 'credit' && (
                  <div className="border-top border-bottom py-3 my-3">
                    <Form.Group className="mb-3">
                      <Form.Label>Customer Information</Form.Label>
                      <Form.Control
                        placeholder="Customer name"
                        value={customer}
                        onChange={(e) => setCustomer(e.target.value)}
                        required
                        size={isMobile ? "sm" : undefined}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Payment Due Date</Form.Label>
                      <Form.Control
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        required
                        size={isMobile ? "sm" : undefined}
                      />
                    </Form.Group>
                  </div>
                )}

                <Button
                  variant="primary"
                  size={isMobile ? "sm" : "lg"}
                  onClick={handleCompleteSale}
                  disabled={selectedItems.length === 0 || loading}
                  className="w-100 py-2 mt-2 d-flex justify-content-center align-items-center gap-2"
                >
                  <CheckCircle size={20} /> 
                  {paymentType === 'cash' 
                    ? 'Review Cash Sale'
                    : 'Review Credit Sale'
                  }
                </Button>
                
                <div className="text-center mt-2 small text-muted">
                  You'll confirm details on the next screen
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </ErrorBoundary>
  );
}